import { useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { GenericComingSoon } from 'components/ComingSoonModal';
import EducationDrawer from 'components/EducationDrawer';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import Toaster from 'components/Toaster';
import CANCEL_SCHEDULED_MESSAGE from '../../gql/mutations/cancel-scheduled-message';
import useCustomerExport from '../../hooks/useCustomerExport';
import CustomerTable from '../CustomerTable';
import ProTipCard from '../ProTipCard';
import SearchSection from '../SearchSection';
import SegmentBreakdown from '../SegmentBreakdown';
import useSegmentBreakdown from '../SegmentBreakdown/hooks/useSegmentBreakdown';
import { SmartInsightsContent } from '../SmartInsights/helpContent';
import AnimatedMetric from './AnimatedMetric';
import useCustomerAnalytics from './hooks/useCustomerAnalytics';
import CustomersSkeleton from './SkeletonLoader';
import {
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  MetricsGrid,
} from './StyledComponents';

const Customers = ({ accountId }) => {
  const history = useHistory();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [activeSegmentFilter, setActiveSegmentFilter] =
    useState('All Customers');

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';
  const [toasterData, setToasterData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSmartInsightsHelp, setShowSmartInsightsHelp] = useState(false);
  const customerTableRef = useRef(null);

  // Initialize the cancel message mutation
  const [cancelScheduledMessage] = useMutation(CANCEL_SCHEDULED_MESSAGE);

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useCustomerExport();

  // Check for success messages on mount
  useEffect(() => {
    const creditSuccessMessage = sessionStorage.getItem('creditSuccessMessage');
    if (creditSuccessMessage) {
      setToasterData({
        title: 'Credit Issued!',
        message: creditSuccessMessage,
        type: 'success',
      });
      // Clear the message from sessionStorage
      sessionStorage.removeItem('creditSuccessMessage');
    }

    // Check for campaign success message
    const campaignSuccessMessage = sessionStorage.getItem(
      'campaignSuccessMessage'
    );
    const campaignMessageId = sessionStorage.getItem('campaignMessageId');
    const campaignScheduled =
      sessionStorage.getItem('campaignScheduled') === 'true';

    if (campaignSuccessMessage) {
      setToasterData({
        title: campaignScheduled ? 'Message Scheduled!' : 'Message Sent!',
        message: campaignSuccessMessage,
        type: 'success',
        persistent: true,
        action: {
          text: 'Undo',
          icon: 'undo',
          onClick: async () => {
            try {
              const { data } = await cancelScheduledMessage({
                variables: {
                  notificationId: parseInt(campaignMessageId),
                },
              });

              if (data?.cancelScheduledMessage?.success) {
                setToasterData({
                  title: 'Message Cancelled',
                  message:
                    data.cancelScheduledMessage.message ||
                    'Your message has been cancelled successfully',
                  type: 'info',
                });
              } else {
                throw new Error(
                  data?.cancelScheduledMessage?.message ||
                    'Failed to cancel message'
                );
              }
            } catch (error) {
              console.error('Failed to cancel message:', error);
              setToasterData({
                title: 'Unable to Cancel',
                message:
                  error.message ||
                  'The message could not be cancelled. It may have already been sent.',
                type: 'error',
              });
            }
          },
        },
      });

      // Clear campaign messages from sessionStorage
      sessionStorage.removeItem('campaignSuccessMessage');
      sessionStorage.removeItem('campaignMessageId');
      sessionStorage.removeItem('campaignScheduled');

      // Auto-hide toaster after 25 seconds
      const timer = setTimeout(() => {
        setToasterData((current) => {
          if (current?.type === 'success') {
            return null;
          }
          return current;
        });
      }, 25000);

      return () => clearTimeout(timer);
    }
  }, [cancelScheduledMessage]);

  // Handle search from SearchSection
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Handle filter change from SearchSection
  const handleFilterChange = useCallback((filter) => {
    // Map filter names to segment values
    const filterMap = {
      'All Customers': '',
      'Season Pass': 'season_pass',
      'Super Fan': 'super_fan',
      'Regular Fan': 'regular_fan',
      'Casual Fan': 'casual_fan',
      Occasional: 'occasional',
      'First-Timer': 'first_timer',
      Inactive: 'inactive',
    };
    setActiveSegmentFilter(filter);
    setSegmentFilter(filterMap[filter] || '');
  }, []);

  // Handle export with selected format
  const handleExport = useCallback(
    async (format) => {
      // Close modal
      setShowExportModal(false);

      // Show processing toaster
      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        // Get current filters from table ref if available
        const tableFilters =
          customerTableRef.current?.getAppliedFilters?.() || {};

        // Build filters object
        const filters = {
          ...tableFilters,
          ...(searchTerm && { search: searchTerm }),
          ...(segmentFilter && { segment: segmentFilter }),
        };

        // Start export mutation with format
        const jobId = await startExport(accountId, filters, format);

        // Subscribe to progress updates
        subscribeToProgress(jobId, {
          onProgress: (status) => {
            // Update toaster with progress
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} customers (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              // Get download URL
              const downloadUrl = await getDownloadUrl(jobId);

              // Show success toaster with download link
              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } customers exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            // Show error toaster
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        // Show error toaster for mutation failure
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      searchTerm,
      segmentFilter,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  const {
    loading,
    error,
    totalCustomers,
    avgLifetimeValue,
    avgEventsPerCustomer,
    avgCustomersPerEvent,
    superFans,
    stats,
  } = useCustomerAnalytics(accountId);

  // Get segment breakdown data
  const { segments: segmentBreakdownData, loading: segmentLoading } =
    useSegmentBreakdown(accountId);

  if (loading) {
    return <CustomersSkeleton />;
  }

  if (error) {
    return (
      <EnhancedThemeProvider>
        <Container>
          <Card>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#EF4444',
              }}
            >
              Error loading analytics: {error.message}
            </div>
          </Card>
        </Container>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderTitle>Customers</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Metrics Overview with Animation */}
        <MetricsGrid>
          <AnimatedMetric
            value={totalCustomers}
            label="Total Customers"
            changeText={stats?.totalCustomers?.changeText || '+0 this week'}
            isPositive={stats?.totalCustomers?.isPositive !== false}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgLifetimeValue}
            label="Avg Lifetime Value"
            changeText={stats?.avgLifetimeValue?.changeText || '+$0'}
            isPositive={stats?.avgLifetimeValue?.isPositive !== false}
            isCurrency={true}
            icon={
              <svg fill="currentColor" viewBox="0 0 16 16">
                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgEventsPerCustomer}
            label="Avg Events/Customer"
            changeText={stats?.avgEventsPerCustomer?.changeText || '+0 events'}
            isPositive={stats?.avgEventsPerCustomer?.isPositive !== false}
            decimals={1}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={avgCustomersPerEvent}
            label="Avg Customers/Event"
            changeText={stats?.avgCustomersPerEvent?.changeText || '+0'}
            isPositive={stats?.avgCustomersPerEvent?.isPositive !== false}
            decimals={0}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
              </svg>
            }
          />

          <AnimatedMetric
            value={superFans}
            label="Super Fan"
            changeText={stats?.superFans?.changeText || '+0 new'}
            isPositive={stats?.superFans?.isPositive !== false}
            icon={
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            }
          />
        </MetricsGrid>

        {/* Dashboard Layout with Table and Sidebar */}
        <DashboardLayout>
          <MainContent>
            {/* Search and Filters */}
            <SearchSection
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              activeFilter={activeSegmentFilter}
            />

            {/* Customer Table */}
            <CustomerTable
              ref={customerTableRef}
              accountId={accountId}
              searchTerm={searchTerm}
              segmentFilter={segmentFilter}
              onClearSegmentFilter={() => {
                setSegmentFilter('');
                setActiveSegmentFilter('All Customers');
              }}
            />
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <div style={{ marginBottom: '30px' }}>
              <QuickActions
                title="Quick Actions"
                actions={[
                  {
                    label: 'Send Campaign',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                      </svg>
                    ),
                    onClick: () => {
                      // Get the actual filtered count and filters from the table
                      const actualFilters =
                        customerTableRef.current?.getAppliedFilters?.() || {};
                      const actualCount =
                        customerTableRef.current?.getTotalCount?.() ||
                        totalCustomers ||
                        0;

                      // Determine the segment name based on what's actually filtered
                      let segmentName = 'All Customers';
                      if (segmentFilter) {
                        // If a predefined segment is selected
                        segmentName = segmentFilter;
                      } else if (
                        searchTerm ||
                        Object.values(actualFilters).some((v) => v)
                      ) {
                        // If custom filters or search are applied
                        segmentName = 'Custom Segment';
                      }

                      // Prepare segment data for the campaign page
                      const segmentData = {
                        name: segmentName,
                        count: actualCount,
                        filter: segmentFilter,
                        searchTerm: searchTerm,
                        appliedFilters: {
                          ...actualFilters,
                          ...(segmentFilter && { segment: segmentFilter }),
                        },
                        isCustomFilter:
                          !!segmentFilter ||
                          !!searchTerm ||
                          Object.values(actualFilters).some((v) => v),
                      };

                      // Navigate to SendCampaign with state
                      history.push(`${routePrefix}/customers/send-campaign`, {
                        segmentData,
                        accountId,
                        returnTo: location.pathname,
                      });
                    },
                  },
                  {
                    component: (
                      <GenericComingSoon>
                        <ButtonV2
                          elevated
                          icon={
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                              <path
                                fillRule="evenodd"
                                d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"
                              />
                            </svg>
                          }
                        >
                          Import Customers
                        </ButtonV2>
                      </GenericComingSoon>
                    ),
                  },
                  {
                    label: 'Export Report',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                      </svg>
                    ),
                    onClick: () => {
                      // Show the export modal
                      setShowExportModal(true);
                    },
                  },
                ]}
              />
            </div>
            {/* Smart Insights - Simple Instructions */}
            <ProTipCard onLearnMore={() => setShowSmartInsightsHelp(true)} />
            {/* Segment Breakdown */}
            <SegmentBreakdown
              segments={segmentBreakdownData}
              loading={segmentLoading}
              onOpenSmartGuide={() => setShowSmartInsightsHelp(true)}
            />
          </Sidebar>
        </DashboardLayout>
      </Container>

      {/* Success Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={toasterData.persistent ? 25000 : 10000}
          persistent={toasterData.persistent}
          action={toasterData.action}
        />
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        recordCount={totalCustomers || 0}
        dataType="customers"
      />

      {/* Smart Marketing Guide */}
      <EducationDrawer
        title="Smart Marketing Guide"
        isOpen={showSmartInsightsHelp}
        onClose={() => setShowSmartInsightsHelp(false)}
        showHelpButton={false}
      >
        <SmartInsightsContent />
      </EducationDrawer>
    </EnhancedThemeProvider>
  );
};

Customers.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Customers;
